<template>
    <div>
        <h3 class="title is-3" style="margin-top:8px;text-transform: capitalize;">
            <router-link to="/tools/featured">
                <b-icon icon="chevron-left" size="is-small"></b-icon>
            </router-link> &nbsp;
            Main Navigation - {{$route.params.type}}
        </h3>
        <div class="navigation-bar">
            <draggable 
                v-model="featuredTopics" 
                :options="{group:'topics', disabled: disabled}" 
                class="columns"
                @choose="checkMoveFeaturedItem"
                @end="resetDisabled"
                >
                <div v-for="element in featuredTopics"
                     :key="element.id"
                     :data-position="element.position"
                     class="column unselectable"
                     @mouseup="resetDisabled">
                     <div class="t-item" :data-position="element.featured_id" @mouseup="resetDisabled">
                        <img :data-position="element.featured_id" 
                             @mouseup="resetDisabled" 
                             :src="element.image_url" 
                             :class="canAssignPosition(element)"
                             :alt="element.name">
                     </div>
                </div>
            </draggable>
            
        </div>
        <hr>
        <button class="button is-primary" @click="savePositions">Save Changes</button>
        <hr>
        <draggable v-model="notFeaturedTopics"  
                   :options="{group:'topics', disabled: disabled}" 
                   @choose="checkMoveToFeatured"
                   @end="resetDisabled"
                   class="columns is-multiline">
            <div v-for="element in notFeaturedTopics"
                  :key="element.id"
                  :class="{'no-drag':element.featured_id < 13}"
                  @mouseup="resetDisabled"
                  class="column is-one-fifth">
                  <div class="t-item" style="text-align:center;" @mouseup="resetDisabled">
                    <img :src="element.image_url" @mouseup="resetDisabled" :alt="element.name">
                  </div>
            </div>
        </draggable>
        <loading v-if="loading"></loading>
    </div>
</template>

<script>
import { GET_TOPICS, UPDATE_POSITIONS } from "@/store/actions";
import draggable from "vuedraggable";
import loading from "@/components/Loading.vue"

export default {
  name: "FeaturedMain",
  components: {
    draggable,
    loading
  },
  data() {
    return {
      featuredTopics: null,
      notFeaturedTopics: null,
      disabled: false,
      loading: false
    };
  },
  computed: {
    data() {
      return this.$store.state.topics.data;
    },
    topics() {
      const { type } = this.$route.params;
      if (!this.data) {
        return null;
      }

      // properly detach
      let topics = JSON.parse(JSON.stringify(this.data));

      topics = topics.filter(
        x => x.enabled && x.sport.toLowerCase() !== "minor"
      );
      if (type !== "all") {
        topics = topics.filter(x => x.sport.toLowerCase() === type);
      }

      return topics;
    }
  },
  mounted() {
    if (!this.data || this.data.length <= 0) {
      this.$store.dispatch(GET_TOPICS).then(res => {
        this.setTopics(this.topics);
        this.setTopicsHidden(this.topics);
      });
    } else {
      this.setTopics(this.topics);
      this.setTopicsHidden(this.topics);
    }
  },
  methods: {
    setTopics(topics) {
      const { type } = this.$route.params;

      topics = topics.filter(x => x.featured_id !== 0);
      topics = topics.sort((a, b) => a.featured_id - b.featured_id);

      if (type === "all") {
        topics = topics.filter(x => x.featured_id < 13);
      }

      topics.forEach(element => {
        element.position = element.featured_id;
      });

      this.featuredTopics = topics;
    },
    setTopicsHidden(topics) {
      const { type } = this.$route.params;

      /**
       * On the main feature page we want to show any topics that are not assigned to the main nav
       */
      if (type === "all") {
        topics = topics.filter(x => x.featured_id > 12 || x.featured_id <= 0);
      } else {
        topics = topics.filter(x => x.featured_id > 19 || x.featured_id <= 0);
      }

      /**
       * Remove topics that are not majors
       */
      topics = topics.filter(
        x =>
          [
            "General",
            "Cycling",
            "Athletics",
            "Aquatics",
            "WWE",
            "Xtra"
          ].indexOf(x.name) === -1
      );
      topics = topics.sort((a, b) => a.featured_id - b.featured_id);
      topics.forEach(element => {
        element.position = 99;
      });
      this.notFeaturedTopics = topics;
    },
    /**
     * Check if the user can move main featured items
     * They can only modify the position of the main items when editing the main navigation
     */
    checkMoveFeaturedItem(e) {
      this.disabled = false;
      const { type } = this.$route.params;
      const itemPosition = e.explicitOriginalTarget.getAttribute(
        "data-position"
      );
      console.log(itemPosition);

      // if the type is all, they can move it
      if (type === "all") {
        return;
      }
      // If the type is for a specific sport, they cannot move it
      if (itemPosition <= 12 && itemPosition !== 0) {
        this.disabled = true;
      }
    },
    /**
     * Check if the user can move an item to the featured tah
     * If there are already 12 items, they can't.
     */
    checkMoveToFeatured() {
      this.disabled = false;
      if (this.featuredTopics.length === 12) {
        this.$warning(
          "There are already 12 items in the featured menu, please remove one to continue"
        );
        this.disabled = true;
      }
    },
    resetDisabled() {
      this.disabled = false;
    },
    /**
     * Make items that the user cannot move grayscale, just so that they know
     */
    canAssignPosition(item) {
      const { type } = this.$route.params;
      if (type === "all") {
        return "";
      }

      if (item.featured_id <= 12 && item.featured_id !== 0) {
        return "disabled";
      }
    },
    savePositions() {
      const { type } = this.$route.params;
      const { featuredTopics } = this;

      // when they are not modifying the main section we want to remove all items that are the main items
      let items =
        type === "all"
          ? featuredTopics
          : featuredTopics.filter(x => x.position >= 13 || x.featured_id === 0);

      let payload = [];

      for (let i = 0; i < items.length; i++) {
        const element = items[i];
        payload.push({
          id: element.id,
          featured_id: i + 1
        });
      }

      this.loading = true;

      this.$store
        .dispatch(UPDATE_POSITIONS, {
          type: "main",
          sport: type,
          payload
        })
        .then(res => {
          if (res.status === 204) {
            this.$success("Positions have been updated successfully");
            this.$store.dispatch(GET_TOPICS).then(res => {
              this.setTopics(this.topics);
              this.setTopicsHidden(this.topics);
            });
          } else {
            this.$warning("Something went wrong, please try again");
          }
        })
        .catch(err => {
          this.$error("An error has occured, please try again");
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.navigation-bar {
  background-color: #f6f6f6;
}
.t-item {
  max-width: 60px;
  max-height: 60px;
}
.unselectable {
  -moz-user-select: -moz-none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.disabled {
  -webkit-filter: grayscale(100%);
  filter: grayscale(100%);
}
</style>